/**
 * Platform Navigation Service
 *
 * Handles API communication for fetching navigation configuration.
 * Navigation is critical infrastructure - errors are thrown (not swallowed)
 * to allow callers to redirect to an error page.
 *
 * Dev toggle: localStorage.setItem('useMockNavigation', 'true') to use mock data
 */

import gatewayService from '@/modules/common/services/gatewayService';
import logServiceError from '@/utils/logServiceError/logServiceError';
import navigationMock from '../mocks/navigationMock.json';

const MOCK_NAV_KEY = 'useMockNavigation';
const baseRoute = '/v3/apps/navigation_items';
const DEFAULT_PAGE_SIZE = 50;

const shouldUseMockData = () => {
  try {
    return localStorage.getItem(MOCK_NAV_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

/**
 * Normalize a single navigation item from dynamicmenus response for frontage.
 *
 * @param {Object} item - Raw item from API
 * @returns {Object} - Item with isBMP and action.payload.newTab flags set for consistent use in store/tree
 */
const normalizeNavigationItem = (item) => {
  if (!item || typeof item !== 'object') return item;
  const isBMP = item.is_bmp === true;

  // TODO-REMOVE: temporary shim – API sends action.params, frontend expects action.payload
  let action = item.action && item.action.params && !item.action.payload
    ? { ...item.action, payload: item.action.params }
    : item.action;

  if (action?.payload) {
    action = { ...action, payload: { ...action.payload, newTab: action.payload.new_tab === true } };
  }

  return {
    ...item,
    isBMP,
    action,
  };
};

const fetchPage = async (page, perPage) => {
  const response = await gatewayService.get(baseRoute, { page, per_page: perPage });
  const data = response.data?.data;
  const items = Array.isArray(data?.navigation_items)
    ? data.navigation_items.map(normalizeNavigationItem)
    : [];
  const total = data?.total_count ?? items.length;
  return { items, total };
};

const SERVICE_NAME = 'platform nav';

const fetchNavigation = async (perPage = DEFAULT_PAGE_SIZE) => {
  if (shouldUseMockData()) {
    console.info('[POV] platform nav: using mock data (localStorage flag)');
    return navigationMock.map(normalizeNavigationItem);
  }

  try {
    const { items: firstPageItems, total } = await fetchPage(1, perPage);
    const fetched = firstPageItems.length;
    const remaining = total - fetched;

    if (window.pov_config?.VUE_APP_ENV !== 'production') {
      console.info(
        `[POV] platform nav: page 1/${Math.ceil(total / perPage)} — fetched ${fetched}, total ${total}, remaining ${remaining}`,
      );
    }

    if (remaining <= 0) return firstPageItems;

    const totalPages = Math.ceil(total / perPage);
    const pageRequests = [];
    for (let page = 2; page <= totalPages; page++) {
      pageRequests.push(fetchPage(page, perPage));
    }

    const results = await Promise.all(pageRequests);

    if (window.pov_config?.VUE_APP_ENV !== 'production') {
      results.forEach(({ items }, i) => {
        console.info(
          `[POV] platform nav: page ${i + 2}/${totalPages} — fetched ${items.length}`,
        );
      });
    }

    const allItems = [firstPageItems, ...results.map((r) => r.items)].flat();

    if (window.pov_config?.VUE_APP_ENV !== 'production') {
      console.info(`[POV] platform nav: all pages fetched — total items: ${allItems.length}`);
    }
    return allItems;
  } catch (error) {
    logServiceError(error, { serviceName: SERVICE_NAME, baseRoute });
    throw error;
  }
};

export default {
  fetchNavigation,
};
